import {
  AlertModal,
  Box,
  Button,
  ErrorModal,
  Flex,
  Image,
  ListItem,
  Modal,
  ModalContent,
  ModalFooter,
  ModalTitle,
  TextLink,
  Toast,
  useOpenModal,
} from '@modusign/design-system';
import { useState } from 'react';
import { useDispatch } from 'react-redux';

import LoadingStatus from '@libs/loading-status';

import iconVertificationConfirm from '@app/assets/icons/icon-vertification-confirm.svg';
import LoadPortoneScript from '@app/components/LoadPortoneScript';
import { CERTIFICATION_STROE_CODE } from '@app/constants/certification';
import { useRequesterMobileIdentification } from '@app/features/contractSetup/hooks';
import useUserIdentificationAndDenyInfo from '@app/hooks/user/useUserIdentificationAndDenyInfo';
import { requestCertification } from '@app/libs/portone/certification';
import { loadAccount } from '@app/store/account/actions';

interface IdentificationModalProps {
  onClose: () => void;
  isOpen: boolean;
}

function IdentificationModal({ onClose, isOpen }: IdentificationModalProps) {
  const openModal = useOpenModal();
  const dispatch = useDispatch();

  const { invalidateIdentificationInfo } = useUserIdentificationAndDenyInfo();

  const [portoneLoadingStatus, setPortoneLoadingStatus] = useState(LoadingStatus.IDLE);

  const { mutate: requesterMobileIdentification } = useRequesterMobileIdentification({
    onSuccess: () => {
      Toast.success({ description: '본인 인증이 완료되었습니다.', duration: 5000 });
      invalidateIdentificationInfo();
      dispatch(loadAccount());
    },
    onError: (error) => {
      openModal((modalProps) => {
        return (
          <ErrorModal
            error={error}
            errorMessageMapper={{
              'User already identified': ['이미 본인인증이 완료된 유저입니다.'],
              'Blocked DI': [
                '서비스 이용이 중지된 사용자입니다. 고객센터로 문의해 주세요.',
                `고객센터 문의시 해당 ID를 첨부해 주세요: ${error.response?.data?.data?.blockedDuplicationInfoId}`,
              ],
            }}
            isOpen
            title="본인 인증 실패"
            onClose={modalProps.onClose}
          />
        );
      });
    },
  });

  const handleAuthentication = async () => {
    const portoneCertificationResponse = await requestCertification({});
    if (portoneCertificationResponse.success) {
      requesterMobileIdentification(portoneCertificationResponse.imp_uid);
      onClose();
    }
  };

  const handleScriptLoad = () => {
    setPortoneLoadingStatus(LoadingStatus.SUCCEEDED);
  };

  const handlePortoneScriptLoadError = () => {
    openModal((modalProps) => (
      <AlertModal
        isOpen={modalProps.isOpen}
        maxWidth="xs"
        title="본인 인증 안내"
        variant="warning"
        onClose={modalProps.onClose}
      >
        <Box mb={6}>
          본인 인증할 수 없습니다.
          <Box as="br" />
          아래 사항들을 확인하고 다시 시도해 주세요.
          <Box as="br" />
          문제가 지속되면 모두싸인 고객센터로 문의해 주세요.
        </Box>
        <Flex
          bg="boxBackgroundNeutralBase"
          border="1px solid"
          borderColor="lightGray300"
          flexDirection="column"
          gap={2}
          p={6}
          textAlign="start"
        >
          <ListItem>네트워크 연결 확인</ListItem>
          <ListItem>보안 프로그램 · 방화벽 점검</ListItem>
          <ListItem>*.iamport.kr 도메인을 화이트리스트에 추가</ListItem>
          <ListItem>모바일에서 셀룰러 데이터를 통해 진행</ListItem>
        </Flex>
      </AlertModal>
    ));
  };

  return (
    <>
      <LoadPortoneScript
        shopId={CERTIFICATION_STROE_CODE}
        onError={handlePortoneScriptLoadError}
        onLoad={handleScriptLoad}
      />
      {portoneLoadingStatus === LoadingStatus.SUCCEEDED && (
        <Modal isOpen={isOpen} maxWidth="xs" onClose={onClose}>
          <Flex justifyContent="center" mb={7} width="100%">
            <Image height="100px" src={iconVertificationConfirm} width="100px" />
          </Flex>
          <ModalTitle mb={7}>본인 인증 안내</ModalTitle>
          <ModalContent mb={8} textAlign="center">
            <Box lineHeight={1.5} mb={4}>
              <Box color="textNeutralBase">
                안전한 전자계약을 위해 일 년에 한 번, 인증이 필요해요.
              </Box>
              <Box color="textNeutralLight">(설정한 내용은 사라지지 않습니다.)</Box>
            </Box>
            <Box width="100%">
              <TextLink
                href="https://support.modusign.co.kr/115720157991"
                rel="noopener noreferrer"
                target="_blank"
              >
                왜 해야 하나요?
              </TextLink>
            </Box>
          </ModalContent>
          <ModalFooter>
            <Button mr={3} onClick={onClose}>
              취소
            </Button>
            <Button variant="primary" onClick={() => handleAuthentication()}>
              휴대폰으로 본인 인증
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </>
  );
}

export default IdentificationModal;
